import type {
    UpdatePanesSizeActionMessage,
    SetRightPaneDisplayAndUpdateSizeActionMessage,
    SetPanesSizeActionMessage,
} from './actions/setPaneDisplayActions';
import type {
    SetPdfJsPasswordStateActionMessage,
    SetWacPreviewStateActionMessage,
    SetVideoPreviewStateActionMessage,
    SetTextPreviewStateActionMessage,
    SetSaveStatusTextActionMessage,
    SetPreviewLoadingActionMessage,
    SetPreviewErrorActionMessage,
    SetPdfJsPreviewStateByDataActionMessage,
    SetPdfJsPreviewStateActionMessage,
    SetNativePdfPreviewStateActionMessage,
    SetImagePreviewStateFromImageGalleryItemActionMessage,
    SetImagePreviewStateActionMessage,
    SetIframePreviewStateActionMessage,
    SetCustomPreviewStateActionMessage,
    SetBlankPreviewStateActionMessage,
    SetAudioPreviewStateActionMessage,
    ResetPreviewPaneActionMessage,
    SetWacReadOnlyActionMessage,
} from './actions/setPreviewPaneStateActions';
import type { SetImagePreviewLoadedActionMessage } from './actions/setImagePreviewLoaded';
import type { SetImageGalleryItemsActionMessage } from './actions/setImageGalleryItems';
import type * as lazyIndex from './lazyIndex';
import { LazyModule, LazyAction } from 'owa-bundling';
import type { default as SxSViewState, RightPaneDisplay } from './store/schema/SxSViewState';
import type {
    default as MsPdfPreviewState,
    MsPdfPasswordState,
} from './store/schema/MsPdfPreviewState';
import type {
    default as PdfJsPreviewState,
    PdfJsPasswordState,
} from './store/schema/PdfJsPreviewState';
import type JsApiInfo from './store/schema/JsApiInfo';
import type ImagePreviewState from './store/schema/ImagePreviewState';
import type ImageGalleryItem from './store/schema/ImageGalleryItem';
import type { PreviewPaneViewStateBase } from './store/schema/PreviewPaneViewState';

export const lazyModule: LazyModule<typeof lazyIndex> = new LazyModule<typeof lazyIndex>(
    () => import(/* webpackChunkName: "SxSData" */ './lazyIndex'),
    { name: 'SxSData' }
);

export const lazyResetPreviewPane: LazyAction<
    (viewState: SxSViewState) => ResetPreviewPaneActionMessage,
    LazyModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.resetPreviewPane);
export const lazySetAudioPreviewState: LazyAction<
    (
        viewState: SxSViewState,
        previewUrl: string,
        optionalParameters?: PreviewPaneViewStateBase
    ) => SetAudioPreviewStateActionMessage,
    LazyModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.setAudioPreviewState);
export const lazySetBlankPreviewState: LazyAction<
    (
        viewState: SxSViewState,
        optionalParameters?: PreviewPaneViewStateBase
    ) => SetBlankPreviewStateActionMessage,
    LazyModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.setBlankPreviewState);
export const lazySetCustomPreviewState: LazyAction<
    (
        viewState: SxSViewState,
        optionalParameters?: PreviewPaneViewStateBase
    ) => SetCustomPreviewStateActionMessage,
    LazyModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.setCustomPreviewState);
export const lazySetIframePreviewState: LazyAction<
    (
        viewState: SxSViewState,
        previewUrl: string,
        optionalParameters?: PreviewPaneViewStateBase
    ) => SetIframePreviewStateActionMessage,
    LazyModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.setIframePreviewState);
export const lazySetImageGalleryItems: LazyAction<
    (
        viewState: ImagePreviewState,
        imageGalleryItems: ImageGalleryItem[]
    ) => SetImageGalleryItemsActionMessage,
    LazyModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.setImageGalleryItems);
export const lazySetImagePreviewLoaded: LazyAction<
    (viewState: ImagePreviewState) => SetImagePreviewLoadedActionMessage,
    LazyModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.setImagePreviewLoaded);
export const lazySetImagePreviewState: LazyAction<
    (
        viewState: SxSViewState,
        previewUrl: string,
        imageGalleryItems: ImageGalleryItem[],
        exportAllowed: boolean,
        printDisabled: boolean,
        optionalParameters?: PreviewPaneViewStateBase
    ) => SetImagePreviewStateActionMessage,
    LazyModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.setImagePreviewState);
export const lazySetImagePreviewStateFromImageGalleryItem: LazyAction<
    (
        imagePreviewState: ImagePreviewState,
        imageGalleryItem: ImageGalleryItem
    ) => SetImagePreviewStateFromImageGalleryItemActionMessage,
    LazyModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.setImagePreviewStateFromImageGalleryItem);
export const lazySetNativePdfPreviewState: LazyAction<
    (
        viewState: SxSViewState,
        previewUrl: string,
        printUrl: string | null,
        mediaType: string,
        optionalParameters?: PreviewPaneViewStateBase
    ) => SetNativePdfPreviewStateActionMessage,
    LazyModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.setNativePdfPreviewState);
export const lazySetPanesSize: LazyAction<
    (
        viewState: SxSViewState,
        leftPaneDimension: number,
        rightPaneDimension: number
    ) => SetPanesSizeActionMessage,
    LazyModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.setPanesSize);
export const lazySetPdfJsPreviewState: LazyAction<
    (
        viewState: SxSViewState,
        previewUrl: string,
        printDisabled: boolean,
        optionalParameters?: PreviewPaneViewStateBase
    ) => SetPdfJsPreviewStateActionMessage,
    LazyModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.setPdfJsPreviewState);
export const lazySetPdfJsPreviewStateByData: LazyAction<
    (
        viewState: SxSViewState,
        data: Uint8Array,
        printDisabled: boolean,
        copyAllowed: boolean,
        exportAllowed: boolean,
        optionalParameters?: PreviewPaneViewStateBase
    ) => SetPdfJsPreviewStateByDataActionMessage,
    LazyModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.setPdfJsPreviewStateByData);
export const lazySetPreviewError: LazyAction<
    (
        viewState: SxSViewState,
        errorMessage: string,
        optionalParameters?: PreviewPaneViewStateBase
    ) => SetPreviewErrorActionMessage,
    LazyModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.setPreviewError);
export const lazySetPreviewLoading: LazyAction<
    (
        viewState: SxSViewState,
        message: string,
        optionalParameters?: PreviewPaneViewStateBase
    ) => SetPreviewLoadingActionMessage,
    LazyModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.setPreviewLoading);
export const lazySetRightPaneDisplayAndUpdateSize: LazyAction<
    (
        viewState: SxSViewState,
        readingPaneDisplay: RightPaneDisplay,
        parentElementWidth?: number
    ) => SetRightPaneDisplayAndUpdateSizeActionMessage,
    LazyModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.setRightPaneDisplayAndUpdateSize);
export const lazySetSaveStatusText: LazyAction<
    (viewState: SxSViewState, text: string) => SetSaveStatusTextActionMessage,
    LazyModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.setSaveStatusText);
export const lazySetTextPreviewState: LazyAction<
    (
        viewState: SxSViewState,
        text: string,
        allowDownload: boolean,
        optionalParameters?: PreviewPaneViewStateBase
    ) => SetTextPreviewStateActionMessage,
    LazyModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.setTextPreviewState);
export const lazySetVideoPreviewState: LazyAction<
    (
        viewState: SxSViewState,
        previewUrl: string,
        optionalParameters?: PreviewPaneViewStateBase
    ) => SetVideoPreviewStateActionMessage,
    LazyModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.setVideoPreviewState);
export const lazySetWacPreviewState: LazyAction<
    (
        viewState: SxSViewState,
        wacUrl?: string,
        jsApiInfo?: JsApiInfo,
        accessToken?: string,
        accessTokenTtl?: number,
        additionalParameters?: string,
        deprecatedTokenAndParameters?: string,
        clickTime?: number,
        readOnly?: boolean,
        optionalParameters?: PreviewPaneViewStateBase | null,
        wdOrigin?: string
    ) => SetWacPreviewStateActionMessage,
    LazyModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.setWacPreviewState);
export const lazySetWacReadOnly: LazyAction<
    (viewState: SxSViewState, readOnly: boolean) => SetWacReadOnlyActionMessage,
    LazyModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.setWacReadOnly);
export const lazyUpdatePanesSize: LazyAction<
    (viewState: SxSViewState, parentElementWidth: number) => UpdatePanesSizeActionMessage,
    LazyModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.updatePanesSize);
export const lazySaveHideReadingPaneOption: LazyAction<
    typeof lazyIndex.saveHideReadingPaneOption,
    LazyModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.saveHideReadingPaneOption);
export const lazySaveDefaultEditCommandOption: LazyAction<
    typeof lazyIndex.saveDefaultEditCommandOption,
    LazyModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.saveDefaultEditCommandOption);
export const lazySetPdfJsPasswordState: LazyAction<
    (
        viewState: PdfJsPreviewState,
        passwordState: PdfJsPasswordState
    ) => SetPdfJsPasswordStateActionMessage,
    LazyModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.setPdfJsPasswordState);
export const lazySetMsPdfPasswordStateMutator: LazyAction<
    (viewState: MsPdfPreviewState, passwordState: MsPdfPasswordState) => void,
    LazyModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.setMsPdfPasswordStateMutator);
export const lazySetMsPdfPreviewStateMutator: LazyAction<
    (
        viewState: SxSViewState,
        previewUrl: string,
        fileSize: number,
        printDisabled: boolean,
        optionalParameters?: PreviewPaneViewStateBase
    ) => void,
    LazyModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.setMsPdfPreviewStateMutator);
export const lazySetMsPdfPreviewStateByDataMutator: LazyAction<
    (
        viewState: SxSViewState,
        data: Uint8Array,
        fileSize: number,
        printDisabled: boolean,
        copyAllowed: boolean,
        exportAllowed: boolean,
        optionalParameters?: PreviewPaneViewStateBase
    ) => void,
    LazyModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.setMsPdfPreviewStateByDataMutator);
export const lazyGetHideReadingPaneOption: LazyAction<
    typeof lazyIndex.getHideReadingPaneOption,
    LazyModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.getHideReadingPaneOption);
export const lazyGetDefaultEditCommandOption: LazyAction<
    typeof lazyIndex.getDefaultEditCommandOption,
    LazyModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.getDefaultEditCommandOption);

export { AutoHideEmailEvent } from './utils/eventNames';
export { MIN_PREVIEW_PANE_WIDTH, RESPONSIVE_READING_PANE_WIDTH } from './utils/sxsConstants';
export type { default as JsApiInfo } from './store/schema/JsApiInfo';
export type { default as LoadedInfo } from './store/schema/LoadedInfo';
export type { default as WacPreviewState } from './store/schema/WacPreviewState';
export type { default as VideoPreviewState } from './store/schema/VideoPreviewState';
export type { default as TextPreviewState } from './store/schema/TextPreviewState';
export { RightPaneDisplay } from './store/schema/SxSViewState';
export type { default as SxSViewState } from './store/schema/SxSViewState';
export { PreviewPaneMode } from './store/schema/PreviewPaneViewState';
export type {
    PreviewPaneErrorViewState,
    PreviewPaneLoadingViewState,
    PreviewPaneViewStateBase,
} from './store/schema/PreviewPaneViewState';
export { PdfJsPasswordState } from './store/schema/PdfJsPreviewState';
export { MsPdfPasswordState } from './store/schema/MsPdfPreviewState';
export type { default as PdfJsPreviewState } from './store/schema/PdfJsPreviewState';
export type { default as MsPdfPreviewState } from './store/schema/MsPdfPreviewState';
export type { default as NativePdfPreviewState } from './store/schema/NativePdfPreviewState';
export type { default as ImagePreviewState } from './store/schema/ImagePreviewState';
export type { default as ImageGalleryItem } from './store/schema/ImageGalleryItem';
export type { ImageGalleryPromise } from './store/schema/ImageGalleryItem';
export type { default as IframePreviewState } from './store/schema/IFramePreviewState';
export type { default as AudioPreviewState } from './store/schema/AudioPreviewState';
